import React, { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Quote, ChevronLeft, ChevronRight, MessageSquareQuote } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";

export default function AtlasTestimonials() {
  const { t, messages } = useI18n();
  const items = messages?.testimonials?.items || [];
  const [idx, setIdx] = useState(0);

  const next = useCallback(
    () => setIdx((i) => (i + 1) % Math.max(items.length, 1)),
    [items.length],
  );
  const prev = useCallback(
    () =>
      setIdx(
        (i) => (i - 1 + Math.max(items.length, 1)) % Math.max(items.length, 1),
      ),
    [items.length],
  );

  // Auto-advance every 7s
  useEffect(() => {
    if (items.length <= 1) return undefined;
    const id = setInterval(next, 7000);
    return () => clearInterval(id);
  }, [next, items.length]);

  if (items.length === 0) return null;
  const current = items[idx];

  return (
    <section
      id="testimonios"
      data-testid="atlas-testimonials"
      className="py-16 lg:py-24 bg-white"
    >
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.18em] text-[#6B46C1] uppercase bg-[#6B46C1]/10 rounded-full px-3 py-1.5">
            <MessageSquareQuote size={14} />
            {t("testimonials.badge")}
          </div>
          <h2
            data-testid="testimonials-title"
            className="mt-4 font-display text-3xl sm:text-4xl lg:text-[44px] font-extrabold text-slate-900 leading-tight"
          >
            {t("testimonials.titleA")}{" "}
            <span className="text-[#6B46C1]">{t("testimonials.titleB")}</span>
          </h2>
        </div>

        <div className="relative mt-12">
          <div className="relative rounded-3xl bg-gradient-to-br from-slate-50 to-white border border-slate-200 shadow-sm p-8 sm:p-12 overflow-hidden">
            <Quote
              size={80}
              className="absolute -top-2 left-6 text-[#6B46C1]/10"
              aria-hidden
            />
            <div
              key={idx}
              data-testid={`testimonial-${idx}`}
              className="relative"
            >
              <p className="font-display text-xl sm:text-2xl text-slate-800 leading-relaxed">
                “{current.quote}”
              </p>
              <div className="mt-8 flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#6B46C1] to-[#8B5CF6] text-white font-display font-extrabold flex items-center justify-center text-lg">
                  {initials(current.author)}
                </div>
                <div>
                  <div className="font-bold text-slate-900">
                    {current.author}
                  </div>
                  <div className="text-sm text-slate-500">
                    {current.role} · {current.company}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={prev}
              aria-label={t("testimonials.prev")}
              data-testid="testimonials-prev-btn"
              className="h-10 w-10 rounded-full"
            >
              <ChevronLeft size={18} />
            </Button>
            <div className="flex items-center gap-1.5 mx-3">
              {items.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  aria-label={`Slide ${i + 1}`}
                  data-testid={`testimonials-dot-${i}`}
                  className={`h-2 rounded-full transition-all ${
                    i === idx
                      ? "w-8 bg-[#6B46C1]"
                      : "w-2 bg-slate-300 hover:bg-slate-400"
                  }`}
                />
              ))}
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={next}
              aria-label={t("testimonials.next")}
              data-testid="testimonials-next-btn"
              className="h-10 w-10 rounded-full"
            >
              <ChevronRight size={18} />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

function initials(name) {
  if (!name) return "";
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("");
}
